import React, { useState, useEffect, useMemo } from 'react';
import { X, Sparkles, Play, Save, Loader2, Users, Building2, AlertCircle } from 'lucide-react';
import { useSearchRun } from '../../hooks/useSearchRun';
import { useSavedSearches } from '../../hooks/useSavedSearches';
import { useAuth } from '../../hooks/useAuth';
import { useContacts } from '../../hooks/useContacts';
import SearchResultsPanel from './SearchResultsPanel';

const SENIORITIES = ['entry', 'senior', 'manager', 'director', 'vp', 'c_suite', 'owner', 'founder'];
const EMPLOYEE_RANGES = ['1,10', '11,50', '51,200', '201,500', '501,1000', '1001,5000', '5001,10000'];

function ChipInput({ values, onChange, placeholder }) {
  const [input, setInput] = useState('');
  const add = () => {
    const v = input.trim();
    if (v && !values.includes(v)) { onChange([...values, v]); setInput(''); }
  };
  return (
    <div className="flex flex-wrap gap-1 items-center border border-slate-300 rounded px-2 py-1 min-h-[34px] bg-white">
      {values.map((v, i) => (
        <span key={i} className="inline-flex items-center gap-1 bg-indigo-100 text-indigo-800 rounded px-1.5 py-0.5 text-[11px]">
          {v}
          <button type="button" onClick={() => onChange(values.filter((_, j) => j !== i))} className="hover:text-red-600">×</button>
        </span>
      ))}
      <input
        type="text" value={input}
        onChange={(e) => setInput(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ',') { e.preventDefault(); add(); } }}
        onBlur={add}
        placeholder={values.length === 0 ? placeholder : ''}
        className="flex-1 min-w-[100px] text-xs outline-none bg-transparent"
      />
    </div>
  );
}

export default function SearchBuilder({ mode = 'create', savedSearch, empresaContext, onClose, onSearchComplete }) {
  const { user } = useAuth();
  const { contatos } = useContacts();
  const { executeSearch, translateQuery, loading, error, results, runId, reset } = useSearchRun();
  const { createSearch } = useSavedSearches();

  const empresasList = useMemo(() => {
    const seen = new Map();
    (contatos || []).forEach((c) => {
      const e = c.empresas;
      if (e?.id && !seen.has(e.id)) seen.set(e.id, e);
    });
    return [...seen.values()].sort((a, b) => (a.company_name || a.empresa || '').localeCompare(b.company_name || b.empresa || ''));
  }, [contatos]);

  const initial = savedSearch?.filters || {};
  const [searchTarget, setSearchTarget] = useState(savedSearch?.search_target || 'people');
  const [empresaId, setEmpresaId] = useState(empresaContext?.id || (savedSearch?.filters?.organization_ids?.[0] ? null : null));
  const [personTitles, setPersonTitles] = useState(initial.person_titles || []);
  const [personSeniorities, setPersonSeniorities] = useState(initial.person_seniorities || []);
  const [personLocations, setPersonLocations] = useState(initial.person_locations || []);
  const [keywords, setKeywords] = useState(initial.q_organization_keyword_tags || []);
  const [orgLocations, setOrgLocations] = useState(initial.organization_locations || []);
  const [employeeRanges, setEmployeeRanges] = useState(initial.organization_num_employees_ranges || []);
  const [nlQuery, setNlQuery] = useState('');
  const [nlReasoning, setNlReasoning] = useState(null);
  const [translating, setTranslating] = useState(false);
  const [autoClassify, setAutoClassify] = useState(true);
  const [autoEnrichTopN, setAutoEnrichTopN] = useState(0);
  const [savePanelOpen, setSavePanelOpen] = useState(false);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [showResults, setShowResults] = useState(false);

  useEffect(() => {
    if (empresaContext?.id) setEmpresaId(empresaContext.id);
  }, [empresaContext]);

  const buildFilters = () => {
    const f = { per_page: 25 };
    if (searchTarget === 'people') {
      if (personTitles.length) f.person_titles = personTitles;
      if (personSeniorities.length) f.person_seniorities = personSeniorities;
      if (personLocations.length) f.person_locations = personLocations;
      f.include_similar_titles = true;
    } else {
      if (keywords.length) f.q_organization_keyword_tags = keywords;
      if (orgLocations.length) f.organization_locations = orgLocations;
      if (employeeRanges.length) f.organization_num_employees_ranges = employeeRanges;
    }
    return f;
  };

  const handleTranslate = async () => {
    if (!nlQuery.trim()) return;
    setTranslating(true);
    const res = await translateQuery(nlQuery.trim(), searchTarget, empresaId);
    setTranslating(false);
    if (!res.success) { alert(`Erro: ${res.error}`); return; }
    const f = res.data.filters || {};
    setNlReasoning({ reasoning: res.data.reasoning, warnings: res.data.warnings, confidence: res.data.confidence });
    if (searchTarget === 'people') {
      setPersonTitles(f.person_titles || []);
      setPersonSeniorities(f.person_seniorities || []);
      setPersonLocations(f.person_locations || []);
    } else {
      setKeywords(f.q_organization_keyword_tags || []);
      setOrgLocations(f.organization_locations || []);
      setEmployeeRanges(f.organization_num_employees_ranges || []);
    }
  };

  const handleExecute = async () => {
    const filters = buildFilters();
    const res = await executeSearch({
      searchTarget, filters,
      savedSearchId: mode === 'execute' ? savedSearch?.id : null,
      empresaContextId: empresaId || null,
      decidedBy: user?.email,
      autoClassify, autoEnrichTopN: Number(autoEnrichTopN) || 0,
    });
    if (res.success) setShowResults(true);
  };

  const handleSave = async () => {
    if (!saveName.trim()) { alert('Nome obrigatório'); return; }
    const res = await createSearch({
      name: saveName.trim(), description: saveDesc.trim() || null,
      search_target: searchTarget, filters: buildFilters(),
      is_shared: false, is_template: false,
      auto_enrich_top_n: Number(autoEnrichTopN) || 0,
    }, user?.email);
    if (!res.success) { alert(`Erro: ${res.error}`); return; }
    setSavePanelOpen(false);
    alert('Audiência salva em ⭐ Minhas.');
  };

  const closeResults = () => { setShowResults(false); reset(); if (onSearchComplete) onSearchComplete(); };

  return (
    <>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] p-4">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col">
          <div className="px-5 py-3 border-b border-slate-200 flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold text-slate-800">
                {mode === 'edit' ? 'Editar audiência' : mode === 'execute' ? `Executar: ${savedSearch?.name || ''}` : 'Nova busca Apollo'}
              </h2>
              {empresaContext && (
                <p className="text-[11px] text-slate-500">Contexto: <strong>{empresaContext.company_name}</strong> ({empresaContext.apollo_id ? `apollo_id: ${empresaContext.apollo_id.slice(0, 8)}...` : 'não enriquecida'})</p>
              )}
            </div>
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={18} /></button>
          </div>

          <div className="flex-1 overflow-auto p-5 space-y-4">
            <section>
              <label className="block text-xs font-medium text-slate-700 mb-1">💬 Descreva em português o que busca</label>
              <div className="flex gap-2">
                <input
                  type="text" value={nlQuery} onChange={(e) => setNlQuery(e.target.value)}
                  placeholder={searchTarget === 'people' ? 'ex: Ache diretores de RE na Brookfield' : 'ex: Incorporadoras médias em SP'}
                  className="flex-1 px-3 py-2 border border-slate-300 rounded text-sm"
                />
                <button onClick={handleTranslate} disabled={!nlQuery.trim() || translating} className="px-3 py-2 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1">
                  {translating ? <Loader2 size={14} className="animate-spin" /> : <Sparkles size={14} />}
                  Traduzir para filtros
                </button>
              </div>
              {nlReasoning && (
                <div className="mt-2 text-[11px] text-slate-600 bg-indigo-50 border border-indigo-100 rounded p-2">
                  <strong>Interpretação:</strong> {nlReasoning.reasoning} · conf {Math.round((nlReasoning.confidence || 0) * 100)}%
                  {nlReasoning.warnings?.length > 0 && (
                    <div className="mt-1 text-amber-700"><AlertCircle size={10} className="inline" /> {nlReasoning.warnings.join(' · ')}</div>
                  )}
                </div>
              )}
            </section>

            <section>
              <label className="block text-xs font-medium text-slate-700 mb-1">Tipo de busca</label>
              <div className="flex gap-2">
                <button onClick={() => setSearchTarget('people')} className={`px-3 py-1.5 text-sm rounded flex items-center gap-1 ${searchTarget === 'people' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  <Users size={14} /> People
                </button>
                <button onClick={() => setSearchTarget('companies')} className={`px-3 py-1.5 text-sm rounded flex items-center gap-1 ${searchTarget === 'companies' ? 'bg-indigo-600 text-white' : 'bg-slate-100 text-slate-600'}`}>
                  <Building2 size={14} /> Companies
                </button>
              </div>
            </section>

            <section>
              <label className="block text-xs font-medium text-slate-700 mb-1">Empresa-alvo (opcional — injeta organization_ids)</label>
              <select value={empresaId || ''} onChange={(e) => setEmpresaId(e.target.value || null)} className="w-full px-3 py-2 border border-slate-300 rounded text-sm bg-white" disabled={!!empresaContext}>
                <option value="">— nenhuma —</option>
                {empresasList.map((e) => (
                  <option key={e.id} value={e.id}>{e.company_name || e.empresa}{e.apollo_id ? ' ✓' : ' (sem apollo_id)'}</option>
                ))}
              </select>
            </section>

            {searchTarget === 'people' ? (
              <>
                <section>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Cargos (person_titles)</label>
                  <ChipInput values={personTitles} onChange={setPersonTitles} placeholder="director, vp real estate, head of acquisitions" />
                </section>
                <section>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Senioridade (person_seniorities)</label>
                  <div className="flex flex-wrap gap-1">
                    {SENIORITIES.map((s) => (
                      <button key={s} type="button" onClick={() => setPersonSeniorities(personSeniorities.includes(s) ? personSeniorities.filter((x) => x !== s) : [...personSeniorities, s])}
                        className={`px-2 py-0.5 text-[11px] rounded border ${personSeniorities.includes(s) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
                        {s}
                      </button>
                    ))}
                  </div>
                </section>
                <section>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Localizações (person_locations)</label>
                  <ChipInput values={personLocations} onChange={setPersonLocations} placeholder="São Paulo, Rio de Janeiro" />
                </section>
              </>
            ) : (
              <>
                <section>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Keywords (q_organization_keyword_tags)</label>
                  <ChipInput values={keywords} onChange={setKeywords} placeholder="real estate development, hospitality" />
                </section>
                <section>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Localizações (organization_locations)</label>
                  <ChipInput values={orgLocations} onChange={setOrgLocations} placeholder="São Paulo, Brazil" />
                </section>
                <section>
                  <label className="block text-xs font-medium text-slate-700 mb-1">Tamanho (funcionários)</label>
                  <div className="flex flex-wrap gap-1">
                    {EMPLOYEE_RANGES.map((r) => (
                      <button key={r} type="button" onClick={() => setEmployeeRanges(employeeRanges.includes(r) ? employeeRanges.filter((x) => x !== r) : [...employeeRanges, r])}
                        className={`px-2 py-0.5 text-[11px] rounded border ${employeeRanges.includes(r) ? 'bg-indigo-600 text-white border-indigo-600' : 'bg-white text-slate-600 border-slate-300'}`}>
                        {r.replace(',', '-')}
                      </button>
                    ))}
                  </div>
                </section>
              </>
            )}

            <section className="bg-slate-50 border border-slate-200 rounded p-3 space-y-2">
              <label className="flex items-center gap-2 text-xs">
                <input type="checkbox" checked={autoClassify} onChange={(e) => setAutoClassify(e.target.checked)} />
                Classificar com IA (recomendado)
              </label>
              {searchTarget === 'people' && (
                <label className="flex items-center gap-2 text-xs">
                  <span>Enriquecer top N automaticamente:</span>
                  <input type="number" min={0} max={10} value={autoEnrichTopN} onChange={(e) => setAutoEnrichTopN(e.target.value)} className="w-16 px-2 py-0.5 border border-slate-300 rounded text-xs" />
                  <span className="text-amber-700">(1 crédito Apollo por enrich)</span>
                </label>
              )}
            </section>

            <details className="text-xs text-slate-600">
              <summary className="cursor-pointer">Preview filtros JSON</summary>
              <pre className="mt-1 bg-slate-100 rounded p-2 text-[10px] overflow-auto">{JSON.stringify(buildFilters(), null, 2)}</pre>
            </details>

            {error && <div className="p-2 bg-red-50 border border-red-200 rounded text-xs text-red-700"><AlertCircle size={12} className="inline" /> {error}</div>}
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex items-center gap-2">
            <button onClick={handleExecute} disabled={loading} className="px-3 py-1.5 text-sm bg-indigo-600 text-white rounded hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-1">
              {loading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
              {loading ? 'Buscando...' : 'Executar agora'}
            </button>
            <button onClick={() => setSavePanelOpen(!savePanelOpen)} className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 rounded hover:bg-slate-200 flex items-center gap-1">
              <Save size={14} /> Salvar como audiência
            </button>
            <button onClick={onClose} className="ml-auto px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
          </div>

          {savePanelOpen && (
            <div className="px-5 py-3 border-t bg-slate-50 space-y-2">
              <input type="text" value={saveName} onChange={(e) => setSaveName(e.target.value)} placeholder="Nome (ex: Diretores RE SP)" className="w-full px-2 py-1 border border-slate-300 rounded text-sm" />
              <input type="text" value={saveDesc} onChange={(e) => setSaveDesc(e.target.value)} placeholder="Descrição (opcional)" className="w-full px-2 py-1 border border-slate-300 rounded text-sm" />
              <div className="flex gap-2">
                <button onClick={handleSave} className="px-3 py-1 text-xs bg-emerald-600 text-white rounded hover:bg-emerald-700">Salvar</button>
                <button onClick={() => setSavePanelOpen(false)} className="px-3 py-1 text-xs text-slate-600 hover:bg-slate-100 rounded">Cancelar</button>
              </div>
            </div>
          )}
        </div>
      </div>
      {showResults && results && (
        <SearchResultsPanel results={results} runId={runId} onClose={closeResults} />
      )}
    </>
  );
}
